import { cx } from "@emotion/css";
import { ArrowRight, Globe, ListPlus, ListX, Lock, Pencil } from "lucide-react";
import { useTranslation } from "react-i18next";
import { generatePath, useHistory } from "react-router-dom";
import { toast } from "react-toastify";
import routing from "routing";
import type { DeckFlashcard } from "src/api/flashcardDecks";
import {
	OutlinedPrimaryButton,
	PrimaryButton,
} from "src/components/Default/LekUI/Button";
import { useModals } from "src/components/Modals/context/ModalProvider";
import { ModalsNames } from "src/components/Modals/context/modals";
import { DeckFlashcardsList } from "src/components/NewComponents/Flashcards/DeckFlashcardsList";
import { DeckLastChange } from "src/components/NewComponents/Flashcards/DeckLastChange";
import { FlashcardCard } from "src/components/NewComponents/Flashcards/FlashcardCard";
import { useCopyDeck, useGetDeckPreview } from "src/hooks/useFlashcards";
import { useFontSize } from "src/hooks/useFontSize";
import FlashcardIcon from "src/images/Icons/newIcons/searchTypes/fish.svg";

import {
	Modal,
	ModalHeader,
	ModalStickyFooter,
} from "./Components/ModalsComponents";

type PreviewFlashcard = Pick<DeckFlashcard, "front" | "back"> &
	Partial<Pick<DeckFlashcard, "id" | "updatedAt">>;

interface DeckPreviewModalProps {
	deckId?: number | null;
	deckName: string;
	deckCode?: string;
	hideStudyButton?: boolean;
	flashcards?: PreviewFlashcard[];
	isInList?: boolean;
	onToggleList?: () => void;
	onClose: () => void;
}

export function DeckPreviewModal({
	deckId,
	deckName,
	deckCode,
	hideStudyButton,
	flashcards,
	isInList,
	onToggleList,
	onClose,
}: DeckPreviewModalProps) {
	const { t } = useTranslation();
	const history = useHistory();
	const { openModal } = useModals();
	const { modalHeaderFontSizeClass, fontSizeClass } = useFontSize();
	const { data, isLoading } = useGetDeckPreview(deckId ?? null);
	const { mutateAsync: copyDeck, isPending: isCopying } = useCopyDeck();
	const sourceFlashcards: PreviewFlashcard[] =
		flashcards ?? data?.flashcards ?? [];
	const isPrivate = data?.permission === "private";
	const PermissionIcon = isPrivate ? Lock : Globe;

	const handleStudy = async () => {
		if (!deckId) return;
		try {
			const { code } = await copyDeck({ deckId });
			onClose();
			history.push(generatePath(routing.paths.flashcardsApp, { deckId: code }));
		} catch {
			toast.error(t("errors.somethingWentWrong", "Coś poszło nie tak"), {
				position: toast.POSITION.BOTTOM_LEFT,
				pauseOnHover: false,
				autoClose: 3000,
			});
		}
	};

	const handleEdit = () => {
		if (!deckCode) return;
		onClose();
		openModal(ModalsNames.EditDeckModal, {
			deckId,
			deckName,
			deckCode,
		});
	};

	const handleToggleList = () => {
		onToggleList?.();
		onClose();
	};

	const hasFooterActions = !!onToggleList || !hideStudyButton;

	return (
		<Modal onClose={onClose} maxWidth="md">
			<ModalHeader
				Icon={FlashcardIcon}
				title={t("decks.detail.preview", "Podgląd zestawu")}
				hasCloseButton
			/>

			<div className="flex justify-between items-center gap-16 mt-24 pb-16 border-bottom-1 flex-col-mobile items-stretch-mobile">
				<div className="flex flex-col gap-4 min-w-0">
					<span className={cx(modalHeaderFontSizeClass, "text-ellipsis-1")}>
						{data?.name ?? deckName}
					</span>
					<span
						className={cx(fontSizeClass, "flex items-center gap-8 text-grey")}
					>
						<span>
							{t("decks.search.flashcardsCount", "{{count}} pojęć", {
								count: sourceFlashcards.length,
							})}
						</span>
						{data && (
							<>
								<span>·</span>
								<span className="flex items-center gap-4">
									<PermissionIcon className="icon-super-small" />
									{isPrivate
										? t("filters.decks.permission.private", "Prywatny")
										: t("filters.decks.permission.public", "Publiczny")}
								</span>
							</>
						)}
					</span>
				</div>
				{deckCode && (
					<OutlinedPrimaryButton
						onClick={handleEdit}
						data-test-id="deck-preview-edit-button"
						className="flex items-center gap-8 flex-shrink-0"
					>
						<Pencil className="icon-super-small" />
						<span>{t("menuActions.flashcards.edit", "Edytuj zestaw")}</span>
					</OutlinedPrimaryButton>
				)}
			</div>

			<DeckFlashcardsList
				flashcards={sourceFlashcards}
				isLoading={isLoading}
				searchTestId="deck-preview-search-input"
				renderItem={(flashcard, query) => (
					<FlashcardCard
						key={flashcard.id ?? `${flashcard.front}-${flashcard.back}`}
						front={flashcard.front}
						back={flashcard.back}
						query={query}
					/>
				)}
			/>

			{hasFooterActions && (
				<ModalStickyFooter className="mt-24 pt-16 border-top-1">
					<div className="container-inline">
						<div className="flex justify-between items-center gap-12 stack-narrow">
							<DeckLastChange
								dates={[
									data?.updatedAt,
									...sourceFlashcards.map((f) => f.updatedAt),
								]}
							/>
							<div className="flex items-center gap-12 stack-narrow">
								{onToggleList && (
									<OutlinedPrimaryButton
										onClick={handleToggleList}
										data-test-id="deck-preview-toggle-list-button"
										className="flex items-center gap-4"
									>
										{isInList ? (
											<ListX className="icon-super-small" />
										) : (
											<ListPlus className="icon-super-small" />
										)}
										<span>
											{isInList
												? t("decks.preview.removeFromList", "Usuń z listy")
												: t("decks.preview.addToList", "Dodaj do listy")}
										</span>
									</OutlinedPrimaryButton>
								)}
								{!hideStudyButton && (
									<PrimaryButton
										onClick={handleStudy}
										isLoading={isCopying}
										disabled={isCopying}
										className="flex items-center gap-8"
										data-test-id="deck-preview-study-button"
									>
										<span>
											{t("decks.preview.study", "Ucz się z tego zestawu")}
										</span>
										<ArrowRight className="icon-super-small" />
									</PrimaryButton>
								)}
							</div>
						</div>
					</div>
				</ModalStickyFooter>
			)}
		</Modal>
	);
}
